<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { usePolicy } from 'dashboard/composables/usePolicy';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import OrgUnitNode from './components/OrgUnitNode.vue';
import UnitFormModal from './components/UnitFormModal.vue';
import UnitMembersModal from './components/UnitMembersModal.vue';
import UnitChannelsModal from './components/UnitChannelsModal.vue';
import RoleFormModal from './components/RoleFormModal.vue';
import UserImportPanel from './components/UserImportPanel.vue';

const store = useStore();
const { t } = useI18n();

const activeTab = ref('structure');

const units = useMapGetter('orgAccess/getUnits');
const unitTree = useMapGetter('orgAccess/getUnitTree');
const roles = useMapGetter('orgAccess/getRoles');
const catalogue = useMapGetter('orgAccess/getCatalogue');
const scopes = useMapGetter('orgAccess/getVisibilityScopes');
const settingKeys = useMapGetter('orgAccess/getSettingKeys');
const defaultSettings = useMapGetter('orgAccess/getDefaultSettings');
const mode = useMapGetter('orgAccess/getMode');
const modes = useMapGetter('orgAccess/getModes');
const uiFlags = useMapGetter('orgAccess/getUIFlags');

const agents = useMapGetter('agents/getAgents');
const teams = useMapGetter('teams/getTeams');
const inboxes = useMapGetter('inboxes/getInboxes');
const { checkPermissions } = usePolicy();

const canManageStructure = computed(() =>
  checkPermissions(['administrator', 'org_structure_manage'])
);
const canManageRoles = computed(() =>
  checkPermissions(['administrator', 'access_role_manage'])
);

const showUnitModal = ref(false);
const showMembersModal = ref(false);
const showChannelsModal = ref(false);
const showRoleModal = ref(false);
const activeUnit = ref(null);
const activeParentId = ref(null);
const activeRole = ref(null);

const selectedMode = ref('off');

const loadAll = async () => {
  await Promise.all([
    store.dispatch('orgAccess/fetchCatalogue'),
    store.dispatch('orgAccess/fetchRoles'),
    store.dispatch('orgAccess/fetchUnits'),
    store.dispatch('agents/get'),
    store.dispatch('teams/get'),
    store.dispatch('inboxes/get'),
  ]);
  selectedMode.value = mode.value;
};

onMounted(loadAll);

const openCreateUnit = parent => {
  activeUnit.value = null;
  activeParentId.value = parent?.id || null;
  showUnitModal.value = true;
};

const openEditUnit = unit => {
  activeUnit.value = unit;
  activeParentId.value = unit.parent_id;
  showUnitModal.value = true;
};

const submitUnit = async payload => {
  try {
    if (payload.id) {
      await store.dispatch('orgAccess/updateUnit', payload);
      useAlert(t('ORG_ACCESS.UNIT_FORM.UPDATED'));
    } else {
      await store.dispatch('orgAccess/createUnit', payload);
      useAlert(t('ORG_ACCESS.UNIT_FORM.CREATED'));
    }
    showUnitModal.value = false;
    await store.dispatch('orgAccess/fetchUnits');
  } catch (error) {
    useAlert(error?.response?.data?.message || error.message);
  }
};

const removeUnit = async unit => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('ORG_ACCESS.STRUCTURE.DELETE_CONFIRM'))) return;
  try {
    await store.dispatch('orgAccess/deleteUnit', unit.id);
    useAlert(t('ORG_ACCESS.UNIT_FORM.DELETED'));
    await store.dispatch('orgAccess/fetchUnits');
  } catch (error) {
    useAlert(error?.response?.data?.message || error.message);
  }
};

const openMembers = unit => {
  activeUnit.value = unit;
  showMembersModal.value = true;
};

const submitMembers = async payload => {
  try {
    await store.dispatch('orgAccess/assignMembers', payload);
    useAlert(t('ORG_ACCESS.MEMBERS.SAVED'));
    showMembersModal.value = false;
  } catch (error) {
    useAlert(error?.response?.data?.message || error.message);
  }
};

const openChannels = unit => {
  activeUnit.value = unit;
  showChannelsModal.value = true;
};

const submitChannels = async payload => {
  try {
    await store.dispatch('orgAccess/attachInboxes', payload);
    useAlert(t('ORG_ACCESS.CHANNELS.SAVED'));
    showChannelsModal.value = false;
  } catch (error) {
    useAlert(error?.response?.data?.message || error.message);
  }
};

const openRole = role => {
  activeRole.value = role;
  showRoleModal.value = true;
};

const submitRole = async payload => {
  try {
    if (payload.id) {
      await store.dispatch('orgAccess/updateRole', payload);
      useAlert(t('ORG_ACCESS.ROLES.UPDATED'));
    } else {
      await store.dispatch('orgAccess/createRole', payload);
      useAlert(t('ORG_ACCESS.ROLES.CREATED'));
    }
    showRoleModal.value = false;
  } catch (error) {
    useAlert(error?.response?.data?.message || error.message);
  }
};

const removeRole = async role => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('ORG_ACCESS.ROLES.DELETE_CONFIRM'))) return;
  try {
    await store.dispatch('orgAccess/deleteRole', role.id);
    useAlert(t('ORG_ACCESS.ROLES.DELETED'));
  } catch (error) {
    useAlert(error?.response?.data?.message || error.message);
  }
};

const saveMode = async () => {
  try {
    await store.dispatch('orgAccess/updateMode', selectedMode.value);
    useAlert(t('ORG_ACCESS.MODE.SAVED'));
  } catch (error) {
    useAlert(error?.response?.data?.message || error.message);
  }
};

const TABS = ['structure', 'roles', 'import', 'mode'];
</script>

<template>
  <SettingsLayout :is-loading="uiFlags.isFetching">
    <template #header>
      <BaseSettingsHeader
        :title="t('ORG_ACCESS.HEADER')"
        :description="t('ORG_ACCESS.DESCRIPTION')"
      >
        <template #actions>
          <Button
            v-if="activeTab === 'structure' && canManageStructure"
            :label="t('ORG_ACCESS.STRUCTURE.ADD_ROOT')"
            @click="openCreateUnit(null)"
          />
          <Button
            v-if="activeTab === 'roles' && canManageRoles"
            :label="t('ORG_ACCESS.ROLES.NEW')"
            @click="openRole(null)"
          />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <div class="flex flex-col gap-6">
        <div class="flex gap-1 p-1 rounded-lg bg-n-alpha-2 w-fit">
          <button
            v-for="tab in TABS"
            :key="tab"
            type="button"
            class="px-3 py-1.5 text-sm rounded-md"
            :class="
              activeTab === tab
                ? 'bg-n-solid-1 text-n-slate-12 font-medium'
                : 'text-n-slate-11'
            "
            @click="activeTab = tab"
          >
            {{ t(`ORG_ACCESS.TABS.${tab.toUpperCase()}`) }}
          </button>
        </div>

        <div v-if="activeTab === 'structure'" class="flex flex-col gap-3">
          <p v-if="!unitTree.length" class="text-sm text-n-slate-11">
            {{ t('ORG_ACCESS.STRUCTURE.EMPTY') }}
          </p>
          <OrgUnitNode
            v-for="unit in unitTree"
            :key="unit.id"
            :unit="unit"
            :can-manage="canManageStructure"
            @add-child="openCreateUnit"
            @edit="openEditUnit"
            @remove="removeUnit"
            @members="openMembers"
            @channels="openChannels"
          />
        </div>

        <div v-if="activeTab === 'roles'" class="flex flex-col gap-2">
          <p v-if="!roles.length" class="text-sm text-n-slate-11">
            {{ t('ORG_ACCESS.ROLES.EMPTY') }}
          </p>
          <div
            v-for="role in roles"
            :key="role.id"
            class="flex flex-wrap items-center gap-3 px-4 py-3 border rounded-lg bg-n-solid-2 border-n-weak"
          >
            <div class="flex flex-col flex-1 min-w-40">
              <span class="text-sm font-medium text-n-slate-12">
                {{ role.name }}
              </span>
              <span class="text-xs text-n-slate-11">
                {{
                  t(`ORG_ACCESS.SCOPES.${role.visibility_scope.toUpperCase()}`)
                }}
                ·
                {{
                  t('ORG_ACCESS.ROLES.PERMISSION_COUNT', {
                    count: role.permissions.length,
                  })
                }}
                ·
                {{
                  t('ORG_ACCESS.STRUCTURE.MEMBER_COUNT', {
                    count: role.members_count,
                  })
                }}
              </span>
            </div>
            <div v-if="canManageRoles" class="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                :label="t('ORG_ACCESS.ROLES.EDIT')"
                @click="openRole(role)"
              />
              <Button
                size="sm"
                variant="ghost"
                color="ruby"
                :disabled="role.system_role"
                :label="t('ORG_ACCESS.ROLES.DELETE')"
                @click="removeRole(role)"
              />
            </div>
          </div>
        </div>

        <UserImportPanel v-if="activeTab === 'import'" />

        <div v-if="activeTab === 'mode'" class="flex flex-col gap-3 max-w-2xl">
          <span class="text-sm font-medium text-n-slate-12">
            {{ t('ORG_ACCESS.MODE.TITLE') }}
          </span>
          <label
            v-for="option in modes"
            :key="option"
            class="flex items-start gap-2 p-3 text-sm border rounded-lg cursor-pointer text-n-slate-12 border-n-weak"
          >
            <input
              v-model="selectedMode"
              type="radio"
              :value="option"
              class="mt-1"
            />
            <span>{{ t(`ORG_ACCESS.MODE.${option.toUpperCase()}`) }}</span>
          </label>
          <div>
            <Button
              :disabled="!canManageRoles"
              :label="t('ORG_ACCESS.UNIT_FORM.SAVE')"
              @click="saveMode"
            />
          </div>
        </div>
      </div>

      <UnitFormModal
        v-model:show="showUnitModal"
        :unit="activeUnit"
        :parent-id="activeParentId"
        :units="units"
        :teams="teams"
        @close="showUnitModal = false"
        @submit="submitUnit"
      />
      <UnitMembersModal
        v-model:show="showMembersModal"
        :unit="activeUnit"
        :agents="agents"
        :roles="roles"
        @close="showMembersModal = false"
        @submit="submitMembers"
      />
      <UnitChannelsModal
        v-model:show="showChannelsModal"
        :unit="activeUnit"
        :inboxes="inboxes"
        @close="showChannelsModal = false"
        @submit="submitChannels"
      />
      <RoleFormModal
        v-model:show="showRoleModal"
        :role="activeRole"
        :roles="roles"
        :catalogue="catalogue"
        :scopes="scopes"
        :setting-keys="settingKeys"
        :default-settings="defaultSettings"
        @close="showRoleModal = false"
        @submit="submitRole"
      />
    </template>
  </SettingsLayout>
</template>
